<script lang="ts" setup>
import type { INavigationItemHeader } from '~/shared/interfaces/ui/navigation'

defineProps<{
  navigationMenu: INavigationItemHeader[]
}>()
</script>

<template>
  <div class="navigation-menu">
    <div
      class="navigation-menu__link"
      v-for="itemMenu in navigationMenu"
      v-show="Object.keys(itemMenu).includes('show') ? itemMenu.show : true"
      :class="itemMenu.hideIcon && 'hidden'"
    >
      <NuxtLink
        :to="itemMenu.href"
        class="svg-fill-black"
        :class="itemMenu.href === '#' && 'not-link'"
        v-on="{ click: itemMenu.clickFunc ? itemMenu.clickFunc : null }"
      >
        <component :is="itemMenu.icon" />
        <span class="navigation-menu__link-text">{{ itemMenu.name }}</span>
        <div
          v-if="itemMenu.indicator?.active"
          class="navigation-menu__link-indicator"
          :class="[itemMenu.indicator.color, itemMenu.indicator.size]"
        >
          <span
            v-if="itemMenu.indicator.text"
            class="navigation-menu__link-indicator__text"
          >
            {{ itemMenu.indicator.text }}
          </span>
        </div>
      </NuxtLink>
      <div
        class="navigation-popup"
        :class="`navigation-popup_${itemMenu.popup}`"
        v-if="itemMenu.popup"
      >
        <div class="navigation-popup__container">
          <slot :name="itemMenu.popup + '-popup'" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'assets/styles/components/navigation/navigation-popup.scss';

.navigation {
  &-menu {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;

    @include md-breakpoint {
      gap: 0;
    }

    &__link {
      position: relative;
      text-align: center;
      color: $content_primary;
      display: flex;
      flex-direction: column;
      gap: 2px;
      align-items: center;
      width: 72px;

      @include screen(1279) {
        width: 40px;
      }

      @include md-breakpoint {
        width: auto;
        flex-grow: 1;
      }

      &.hidden {
        display: none;
      }

      a {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;

        &.router-link-active {
          @include md-breakpoint {
            color: $control-content_orange;

            svg {
              :deep(path) {
                fill: $control-content_orange;
              }
            }
          }
        }

        &.not-link {
          pointer-events: none;
        }
      }

      @include md-breakpoint {
        a {
          color: $content_secondary;
        }

        &:not(:last-child) {
          margin-right: 0;
        }
      }

      .navigation-popup {
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
      }

      &:hover {
        .navigation-popup {
          visibility: visible;
          opacity: 1;
          pointer-events: auto;
          z-index: 110;
        }
      }

      &-text {
        @include font(14px, 20px, 400);

        @include screen(1279) {
          display: none;
        }

        @include md-breakpoint {
          display: block;
          @include font(12px, 14px, 400);
        }
      }

      &-indicator {
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        left: 28px;
        top: -2px;
        border-radius: 20px;
        border: 2px $control-border_white solid;
        text-align: center;
        height: 18px;
        min-width: 18px;
        padding: 0 4px;
        color: $content_white;
        @include font(12px, 16px, 500);

        @include screen(1279) {
          left: 12px;
        }

        @include md-breakpoint {
          left: 24px;
        }

        &.orange {
          background: $control-bg_primary;
        }
      }
    }
  }
}
</style>
